// src/pages/StartGate.jsx
// Description: First screen on app open (pre-login). Same look as WorldSelector,
// but with 2 cards: Studio + Visitor. Sets mudo:userRole and routes to /auth.

import React from "react";
import { useNavigate } from "react-router-dom";

// Logo and images
import mudoLogo from "../assets/mudo-logo-bg.png";
import architectureImg from "../assets/worlds/mudo_architecture.png";
import adventureImg from "../assets/worlds/mudo_adventure.png";

export default function StartGate() {
  const navigate = useNavigate();

  const go = (role) => {
    localStorage.setItem("mudo:userRole", role);
    navigate(`/auth?mode=${role}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background glow (keep subtle, similar to your screenshot vibe) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[520px] rounded-full blur-[120px] opacity-25 bg-amber-500" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/70 to-black" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex flex-col items-center text-center">
          {/* Logo */}
          <div className="mb-6 flex justify-center" style={{ height: '20vh' }}>
            <img
              src={mudoLogo}
              alt="Mudo"
              className="h-full w-auto select-none"
              draggable={false}
            />
          </div>

          <div className="mt-10">
            <h2 className="text-2xl md:text-3xl font-semibold">
              Choose how you're using Mudo
            </h2>
            <p className="mt-3 text-white/70 max-w-2xl mx-auto">
              One Mudo account, two focused experiences — a free visitor world and a studio world
              with subscription tools.
            </p>
            <p className="mt-2 text-xs text-white/45">
              Built for studios and makers.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <RoleCard
            badge="STUDIO"
            title="Studio"
            description="Planner, kiln, products, sales, and studio operations."
            cta="Enter"
            imageSrc={architectureImg}
            onClick={() => go("studio")}
          />

          <RoleCard
            badge="VISITOR"
            title="Visitor"
            description="Discover makers, browse, book workshops and experiences."
            cta="Enter"
            imageSrc={adventureImg}
            onClick={() => go("visitor")}
          />
        </div>

        {/* Footer */}
        <div className="mt-14 text-center text-xs text-white/40">
          Brought to you by{" "}
          <a
            href="https://www.barklinestudio.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 hover:text-amber-400 transition"
          >
            Bark Line Studio
          </a>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ badge, title, description, cta, imageSrc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/7 transition overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
    >
      <div className="relative h-40 md:h-44 overflow-hidden">
        <img
          src={imageSrc}
          alt=""
          className="w-full h-full object-cover opacity-85 group-hover:opacity-95 transition"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute top-3 left-3 text-[10px] tracking-widest text-white/60 bg-black/35 border border-white/10 px-2 py-1 rounded-full">
          {badge}
        </div>
      </div>

      <div className="p-5">
        <div className="text-lg font-semibold">{title}</div>
        <div className="mt-1 text-sm text-white/65">{description}</div>

        <div className="mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 bg-amber-500/90 text-black font-semibold text-sm group-hover:bg-amber-400 transition">
          {cta}
          <span className="opacity-70">→</span>
        </div>
      </div>
    </button>
  );
}
